import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useLoanContext } from "../LoanContext";
import TableWithPagination from "../../../components/TableWithPagination";
import StatusChip from "../../../components/styled/StatusChip";
import { formatAmount } from "../../../utils/formatters";
import { formatDateToDDMMYYYY } from "../../../utils/dateUtils";

interface Bill {
  dueDate?: Date;
  dueAmount?: number;
  paymentAmount?: number;
  paymentDate?: Date;
  paymentSatisfied?: boolean;
  loanAccount?: string;
  status?: string;
}

const BillsPage: React.FC = () => {
  const [bills, setBills] = useState<Bill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { loan } = useLoanContext();
  const { loanId } = useParams(); // Get loanId directly from URL params


  useEffect(() => {
    
    if (!loanId || !loan) {
      if (!loanId) {
        setError("No loanId provided.");
      } else if (!loan) {
        setError("Loading loan data...");
      }
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");
    axios
      .get(`/api/bills?loanId=${loanId}`, {
        withCredentials: true,
      })
      .then((res) => {
        // The backend now returns transformed data directly
        const processedBills = res.data.map((bill: Record<string, unknown>) => {
          let status = "";

          if (bill.paymentSatisfied === true) {
            status = "Paid";
          } else {
            const paymentAmt = Number(bill.paymentAmount || 0);
            const dueAmt = Number(bill.dueAmount || 0);

            if (paymentAmt === 0) {
              status = "Pending";
            } else if (paymentAmt < dueAmt) {
              status = "Partially Paid";
            } else {
              status = "Unknown";
            }
          }

          return { ...bill, status: status };
        });
        setBills(processedBills);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.response?.data?.error || "Failed to fetch bills.");
        setLoading(false);
      });
  }, [loanId]);

  if (loading)
    return (
      <Box textAlign="center" mt={8}>
        <Typography color="#fff">Loading loans...</Typography>
      </Box>
    );
  if (error)
    return (
      <Box textAlign="center" mt={8}>
        <Typography color="error.main">{error}</Typography>
      </Box>
    );

  return (
    <>
      <TableWithPagination
        columns={[
          { 
            label: "Due Date", 
            key: "dueDate", 
            sortable: true,
            renderCell: (row: Record<string, unknown>) => row.dueDate ? formatDateToDDMMYYYY(row.dueDate as string) : ''
          },
          { 
            label: "Due Amount", 
            key: "dueAmount", 
            sortable: true,
            renderCell: (row: Record<string, unknown>) => formatAmount(row.dueAmount as number)
          },
          { 
            label: "Payment Date", 
            key: "paymentDate",
            renderCell: (row: Record<string, unknown>) => row.paymentDate ? formatDateToDDMMYYYY(row.paymentDate as string) : ''
          },
          { 
            label: "Payment Amount", 
            key: "paymentAmount",
            renderCell: (row: Record<string, unknown>) => formatAmount(row.paymentAmount as number)
          },
          {
            label: "Payment Status",
            key: "status",
            renderCell: (row: Record<string, unknown>) => (
              <StatusChip status={row.status as string}>{row.status as string}</StatusChip>
            )
          }
        ]}
        rows={bills as Record<string, unknown>[]}
        getRowKey={(row: Record<string, unknown>) => row.Name as string}
        rowsPerPage={10}
      />
    </>
  );
};

export default BillsPage;
